import type { ListKind } from '@bbbk/schema';
import type { FilterOption } from '../common';
import type { BookingsFilter, DateWindow } from './types';
import { STATUS_FILTER_OPTIONS, VIEWING_STATUS_OPTIONS } from './constants';

// THE ONE CANONICAL ALL (viewings-in-the-bookings-list AC9; frame.md, Empty
// filter). The Type filter is a set over the two kinds. Nothing chosen is All;
// both chosen is the same set as none and is normalised to it on write, so
// there is one All and not two — deselecting the last kind is All by
// definition and needs no guard, and Clear is All too.

export type ListState = 'all' | 'booking' | 'viewing';

export function isAll(types: readonly ListKind[]): boolean {
  return listKindOf(types) === 'all';
}

export function normaliseTypes(types: readonly ListKind[]): ListKind[] {
  const set = new Set(types);
  if (set.size === 0 || set.size === 2) return [];
  return [...set];
}

export function listKindOf(types: readonly ListKind[]): ListState {
  const set = new Set(types);
  if (set.size === 1) return set.has('viewing') ? 'viewing' : 'booking';
  return 'all';
}

// Status follows Type (AC9, AC10): one kind's own vocabulary, or BOTH under All
// — a status belongs to one kind, so choosing "Expected" under All narrows the
// list to the expected viewings and "Arrived" to the arrived bookings (Sir,
// 2026-09-02). The control is disabled only while the Date window is present.
export function statusOptionsFor(types: readonly ListKind[]): FilterOption[] {
  switch (listKindOf(types)) {
    case 'booking':
      return STATUS_FILTER_OPTIONS;
    case 'viewing':
      return VIEWING_STATUS_OPTIONS;
    default:
      return [...STATUS_FILTER_OPTIONS, ...VIEWING_STATUS_OPTIONS];
  }
}

// Which kind a status belongs to — the booking vocabulary or the viewing one.
export function statusKindOf(status: string): ListKind | null {
  if (STATUS_FILTER_OPTIONS.some((o) => o.key === status)) return 'booking';
  if (VIEWING_STATUS_OPTIONS.some((o) => o.key === status)) return 'viewing';
  return null;
}

// A status survives a Type change only where the new kind owns it; All owns
// both (AC8b).
export function statusSurvives(status: string | null, types: readonly ListKind[]): boolean {
  if (status === null) return true;
  const kind = listKindOf(types);
  return kind === 'all' || statusKindOf(status) === kind;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayMonth = (iso: string): string => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
};

// What the Date control says (AC11). "Next 7 days" when the window is the
// coming week from today — the panel's own words — and the two dates otherwise,
// because a window handed over from another day is not "next" anything.
export function windowLabel(window: DateWindow, today: string): string {
  const days = Math.round((Date.parse(window.to + 'T00:00:00Z') - Date.parse(window.from + 'T00:00:00Z')) / 86_400_000);
  if (window.from === today && days === 7) return 'Next 7 days';
  return `${dayMonth(window.from)} – ${dayMonth(window.to)}`;
}

// What counts as an active filter — so an empty list under a narrowed Type or
// a handed-over window offers "Clear filters" rather than claiming the kennel
// has no bookings (frame.md, landmine 2; `inOn` was missing from this test
// before, and is not any more).
export function isFilterActive(f: BookingsFilter): boolean {
  return Boolean(f.q || f.status || f.area || f.inOn || f.window || !isAll(f.types));
}
